import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    FileText,
    CloudUpload,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ChevronsDownUp,
    ChevronsUpDown,
    Ruler,
    FolderOpen,
    Camera,
    Receipt,
    Menu,
    Trash2,
    Loader2,
    Sparkles,
    Brain,
    Calculator,
    Scissors,
    Pin,
    PinOff,
    GripVertical,
    Pencil,
    Search,
    BookMarked,
    X as XIcon,
} from "lucide-react";
import TopNav from "../components/TopNav";
import UploadPlansDialog from "../components/UploadPlansDialog";
import PdfViewer from "../components/PdfViewer";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "../components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import ScaleIndicator from "../components/ScaleIndicator";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { formatApiError } from "../lib/auth";
import { api } from "../lib/api/_client";
import { getProject, listUploads, deleteUpload, reorderUploads, listAnalyses, analyzeProject, listEstimates, createEstimate, createTakeoff, listTakeoffItems, cancelAiJob, getActiveAiJob, pollAiJob, getAiJob } from "../lib/api";
import { listContacts } from "../lib/api/contacts";
import { updateProject } from "../lib/api/projects";
import { UserPlus, Users } from "lucide-react";
import AnalyzeDialog from "../components/AnalyzeDialog";
import AnalysisResultDialog from "../components/AnalysisResultDialog";
import AnalyzingOverlay from "../components/AnalyzingOverlay";
import EstimateDialog from "../components/EstimateDialog";
import EstimateResultDialog from "../components/EstimateResultDialog";
import ProjectFilesPanel from "../components/ProjectFilesPanel";
import SortableDocRow from "../components/workspace/SortableDocRow";
import SortablePinnedChildRow from "../components/workspace/SortablePinnedChildRow";
import TakeoffItemsPanel from "../components/workspace/TakeoffItemsPanel"; // kept for future use; inline rendering now lives in SortableDocRow
void TakeoffItemsPanel;
import AIToolbar from "../components/workspace/AIToolbar";
import PrintPreviewDialog from "../components/workspace/PrintPreviewDialog";
import SepticDesignSheet from "../components/workspace/SepticDesignSheet";
import WorkspaceCanvas from "../components/workspace/WorkspaceCanvas";
import { useWorkspaceUploads } from "../hooks/useWorkspaceUploads";
import { useTakeoffState } from "../components/workspace/useTakeoffState";
import { useFileActions } from "../components/workspace/useFileActions";
import { DocsToolbar } from "../components/workspace/DocsToolbar";

function isImage(f) {
    return (f?.content_type || "").startsWith("image/");
}
function isPdf(f) {
    return (f?.content_type || "").includes("pdf");
}

export default function WorkspacePage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [project, setProject] = useState(null);
    const {
        files, setFiles,
        activeFileId, setActiveFileId,
        activeFile,
        activePage, setActivePage,
        expandedPdfs, setExpandedPdfs,
        uploadOpen, setUploadOpen,
        pendingDeleteFile, setPendingDeleteFile,
        deletingFile, setDeletingFile,
        splittingIds, setSplittingIds,
        pinningSubpage, setPinningSubpage,
    } = useWorkspaceUploads();
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    // Planswift-style "Pages, Bookmarks" search filter — scoped to file
    // display names + takeoff labels. Empty string = no filter.
    const [sidebarSearch, setSidebarSearch] = useState("");
    // iter-170: plugin toggles moved out of the workspace bar and into
    // the Estimate/Analyze dialogs. Each dialog owns its own picker and
    // persists selection per-project via PUT /projects/:id/plugins, so
    // no shared state is needed here anymore.

    // AI Analyze
    const [analyzeOpen, setAnalyzeOpen] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analyses, setAnalyses] = useState([]);
    const [activeAnalysisId, setActiveAnalysisId] = useState(null);
    // iter-31: single source of truth for "which file is in inline-rename
    // mode". Driven by the DocsToolbar "Rename" button (which targets the
    // selected file). Passed into SortableDocRow so it can show the input.
    const [renamingFileId, setRenamingFileId] = useState(null);
    const activeAnalysis = useMemo(
        () => analyses.find((a) => a.id === activeAnalysisId) || null,
        [analyses, activeAnalysisId],
    );

    // AI Estimate
    const [estimateOpen, setEstimateOpen] = useState(false);
    // iter-168 — As-Built creation state. Button next to Estimate calls
    // POST /projects/{id}/asbuilt; server returns existing record if any
    // (one-per-project guard) so the button doubles as "open as-built".
    const [asBuiltLoading, setAsBuiltLoading] = useState(false);
    // iter-169 — Schedule 1/2/3 quick-open. Same pattern as the
    // as-built button: idempotent POST returns existing or freshly
    // created + pre-filled PDF as an upload. `scheduleLoading` holds
    // the schedule number currently being fetched so the button can
    // show a spinner and disable itself.
    const [scheduleLoading, setScheduleLoading] = useState(0);
    const handleOpenSchedule = async (n) => {
        if (!projectId || scheduleLoading) return;
        setScheduleLoading(n);
        try {
            // iter-169c — When the fill logic changes we bump this
            // session key so every session gets ONE fresh regenerate
            // per schedule, then subsequent opens are idempotent.
            // iter-169e — bumped again after the Schedule 2 template
            // rebuild + `_apply_field_values` fix.
            const key = `schedule-regen-v169e-${projectId}-${n}`;
            const alreadyRegen = window.sessionStorage.getItem(key);
            const shouldRegen = !alreadyRegen;
            const url = shouldRegen
                ? `/projects/${projectId}/schedules/${n}?regen=1`
                : `/projects/${projectId}/schedules/${n}`;
            const { data } = await api.post(url);
            if (shouldRegen) window.sessionStorage.setItem(key, "1");
            setFiles((prev) => {
                const without = prev.filter((f) => f.id !== data.id);
                return [data, ...without];
            });
            setActiveFileId(data.id);
            toast.success(`Schedule ${n} opened`);
        } catch (err) {
            toast.error(formatApiError(err) || `Schedule ${n} failed.`);
        } finally {
            setScheduleLoading(0);
        }
    };
    // iter-169e — Schedule field editor: pdf.js annotation layer
    // rendered on Schedule 1/2/3 uploads emits change events through
    // PdfViewer -> WorkspaceCanvas -> here. We batch them per file
    // and debounce-save so the user can type without a network call
    // on every keystroke.
    const scheduleSaveTimerRef = useRef(null);
    const schedulePendingRef = useRef({}); // { [uploadId]: { field: val } }
    const handleScheduleFieldChange = useCallback((name, value) => {
        const af = files.find((f) => f.id === activeFileId);
        if (!af || !af.schedule_no) return;
        const bucket = schedulePendingRef.current[af.id] || {};
        bucket[name] = value;
        schedulePendingRef.current[af.id] = bucket;
        if (scheduleSaveTimerRef.current) {
            clearTimeout(scheduleSaveTimerRef.current);
        }
        scheduleSaveTimerRef.current = setTimeout(async () => {
            const pending = schedulePendingRef.current;
            schedulePendingRef.current = {};
            for (const [uploadId, fields] of Object.entries(pending)) {
                try {
                    const { data } = await api.patch(
                        `/uploads/${uploadId}/schedule-fields`,
                        { fields },
                    );
                    setFiles((prev) =>
                        prev.map((f) => (f.id === uploadId
                            ? { ...f, schedule_field_values: data.schedule_field_values, updated_at: data.updated_at }
                            : f)),
                    );
                } catch (err) {
                    toast.error(formatApiError(err) || "Save failed");
                }
            }
        }, 800);
    }, [files, activeFileId]);

    // iter-169h — Save a drawn applicant signature to the schedule PDF.
    // iter-169i — Also persist to user profile when the "Save for
    // future schedules" toggle was ticked in the pad.
    const handleSaveSignature = useCallback(async (uploadId, pngDataUrl, saveToProfile) => {
        try {
            const { data } = await api.post(
                `/uploads/${uploadId}/schedule-signature`,
                { png: pngDataUrl },
            );
            setFiles((prev) =>
                prev.map((f) => (f.id === uploadId
                    ? { ...f, signature_png: data.signature_png, updated_at: data.updated_at }
                    : f)),
            );
            if (saveToProfile) {
                try {
                    await api.patch("/auth/company-profile", {
                        saved_signature_png: pngDataUrl,
                    });
                    toast.success("Signature saved to profile");
                } catch (err) {
                    toast.error(formatApiError(err) || "Profile save failed");
                }
            } else {
                toast.success("Signature saved");
            }
        } catch (err) {
            toast.error(formatApiError(err) || "Signature save failed");
        }
    }, []);

    // iter-169g — Print / flatten schedule.
    // iter-169j — opens a lightbox preview with a Download button so
    // users can double-check the flattened PDF before printing.
    // iter-169k — Septic design side-sheet
    const [septicOpen, setSepticOpen] = useState(false);
    const handleSaveSepticDesign = useCallback(async (patch) => {
        if (!projectId) return;
        try {
            const { data } = await api.patch(`/projects/${projectId}`, patch);
            setProject(data);
            toast.success("Septic design saved");
        } catch (err) {
            toast.error(formatApiError(err) || "Save failed");
        }
    }, [projectId]);

    const [printPreview, setPrintPreview] = useState({ open: false, url: null, filename: "" });
    const handlePrintSchedule = useCallback(async (uploadId) => {
        try {
            const res = await api.post(
                `/uploads/${uploadId}/schedule-flatten`,
                null,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const af = files.find((f) => f.id === uploadId);
            const filename = ((af?.display_name || "schedule").replace(/[^A-Za-z0-9._-]+/g, "_")) + "_flat.pdf";
            setPrintPreview({ open: true, url, filename });
        } catch (err) {
            toast.error(formatApiError(err) || "Flatten failed");
        }
    }, [files]);
    const handleClosePrintPreview = useCallback(() => {
        setPrintPreview((prev) => {
            if (prev.url) setTimeout(() => URL.revokeObjectURL(prev.url), 500);
            return { open: false, url: null, filename: "" };
        });
    }, []);
    const handleDownloadFlat = useCallback(() => {
        if (!printPreview.url) return;
        const a = document.createElement("a");
        a.href = printPreview.url;
        a.download = printPreview.filename;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("Flat PDF downloaded");
    }, [printPreview]);

    // iter-169m — Batch print pack: download S1+S2+S3 flattened in a ZIP.
    const handlePrintPack = useCallback(async () => {
        if (!projectId) return;
        try {
            const res = await api.post(
                `/projects/${projectId}/schedules-print-pack`,
                null,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const slug = (project?.name || "project").replace(/[^A-Za-z0-9._-]+/g, "_");
            a.download = `${slug}_schedules_pack.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            toast.success("Print pack downloaded");
        } catch (err) {
            toast.error(formatApiError(err) || "Pack failed");
        }
    }, [projectId, project]);

    const handleOpenAsBuilt = async () => {
        if (asBuiltLoading || !projectId) return;
        setAsBuiltLoading(true);
        try {
            const { data } = await api.post(`/projects/${projectId}/asbuilt`);
            setFiles((prev) => {
                const without = prev.filter((f) => f.id !== data.id);
                return [data, ...without];
            });
            setActiveFileId(data.id);
            toast.success(`As-Built ${data.asbuilt_fields?.drawing_no || "created"}`);
        } catch (err) {
            toast.error(formatApiError(err) || "As-Built failed.");
        } finally {
            setAsBuiltLoading(false);
        }
    };
    const [estimating, setEstimating] = useState(false);
    const [estimates, setEstimates] = useState([]);
    const [activeEstimateId, setActiveEstimateId] = useState(null);
    const activeEstimate = useMemo(
        () => estimates.find((e) => e.id === activeEstimateId) || null,
        [estimates, activeEstimateId],
    );

    // AI Takeoff (PlanSwift-style shapes drawn on PDF) — all state +
    // handlers live in the extracted hook so this component stays
    // focused on layout / orchestration (iter-103 refactor).
    const {
        takeoffItems, setTakeoffItems,
        takingOff, setTakingOff,
        selectedTakeoffId, setSelectedTakeoffId,
        takeoffHistory,
        undoing,
        drawMode, setDrawMode,
        drawTrade, setDrawTrade,
        drawPoints, setDrawPoints,
        drawSaving,
        aiAssistPendingId,
        takeoffItemsByUploadId,
        pushTakeoffHistory,
        handleDeleteTakeoffItem,
        handleCommitTakeoffPoints,
        handleReclassifyTakeoffItem,
        handleUndoTakeoff,
        handleDrawPoint,
        handleDrawCancel,
        handleDrawFinish,
        handleToggleTakeoffVisibility,
        handleAiAssistTakeoffItem,
        setPdfPageSize,
    } = useTakeoffState({
        project,
        activeFile,
        loadAll: () => loadAll(),
    });
    const [filesOpen, setFilesOpen] = useState(false);
    // iter-98: independent open state per Files / Photos / Receipts bucket
    const [photosOpen, setPhotosOpen] = useState(false);
    const [receiptsOpen, setReceiptsOpen] = useState(false);
    const filesById = useMemo(() => {
        const m = {};
        for (const f of files) m[f.id] = f;
        return m;
    }, [files]);

    // Live per-step AI-job progress surfaced in the AnalyzingOverlay.
    const [jobStage, setJobStage] = useState("");
    const [jobStagePct, setJobStagePct] = useState(null);
    const [jobUsage, setJobUsage] = useState(null);
    // iter-214 — page scan counter driven by backend extraction.
    const [jobPages, setJobPages] = useState({ scanned: null, total: null });
    // Current in-flight AI job id — non-null = Cancel button is active.
    const [activeJobId, setActiveJobId] = useState(null);

    const handleJobStage = ({ stage, progress_pct, usage, pages_scanned, pages_total }) => {
        setJobStage(stage || "");
        setJobStagePct(typeof progress_pct === "number" ? progress_pct : null);
        if (usage) setJobUsage(usage);
        if (typeof pages_scanned === "number" || typeof pages_total === "number") {
            setJobPages({
                scanned: typeof pages_scanned === "number" ? pages_scanned : null,
                total: typeof pages_total === "number" ? pages_total : null,
            });
        }
    };

    const runAnalyze = async (payload) => {
        setAnalyzing(true);
        setJobStage("Queuing analysis…");
        setJobStagePct(0);
        setJobUsage(null);
        try {
            const record = await analyzeProject(
                { ...payload, project_id: projectId },
                handleJobStage,
                setActiveJobId,
            );
            setAnalyses((prev) => [record, ...prev]);
            setActiveAnalysisId(record.id);
            toast.success("Analysis complete");
            if (payload.auto_pin && (record.auto_pinned_ids || []).length > 0) {
                toast.message(
                    `Pinned ${record.auto_pinned_ids.length} page${record.auto_pinned_ids.length === 1 ? "" : "s"} to the top`,
                );
                loadAll();
            }
        } catch (err) {
            if (err?.canceled) {
                toast.message("Analysis canceled");
            } else {
                const msg =
                    err?.response?.data?.detail || err?.message || "Analysis failed";
                toast.error(msg);
            }
        } finally {
            setAnalyzing(false);
            setJobStage("");
            setJobStagePct(null);
            setActiveJobId(null);
        }
    };

    const runTakeoff = async (payload) => {
        setTakingOff(true);
        setJobStage("Queuing takeoff…");
        setJobStagePct(0);
        setJobUsage(null);
        try {
            const result = await createTakeoff(
                {
                    project_id: projectId,
                    document_ids: payload.document_ids || null,
                    user_instructions: payload.extra_notes || "",
                },
                handleJobStage,
                setActiveJobId,
            );
            const items = result?.items || [];
            setTakeoffItems((prev) => [...items, ...prev]);
            if (items.length > 0) {
                toast.success(
                    `Takeoff drew ${items.length} shape${items.length === 1 ? "" : "s"} across ${new Set(items.map((i) => i.upload_id)).size} page${new Set(items.map((i) => i.upload_id)).size === 1 ? "" : "s"}`,
                );
                loadAll();
            } else {
                toast.message("Takeoff finished — no quantifiable geometry detected on the selected pages.");
            }
            return items;
        } catch (err) {
            if (err?.canceled) {
                toast.message("Takeoff canceled");
                return [];
            }
            const msg = err?.response?.data?.detail || err?.message || "Takeoff failed";
            toast.error(msg);
            return [];
        } finally {
            setTakingOff(false);
            setActiveJobId(null);
        }
    };

    // iter-202 — Big-job survival. On a context-overflow error from the
    // backend we silently re-fire the estimate (backend will shrink the
    // extract harder on the next pass). On a network error during
    // polling the pollAiJob loop already auto-retries forever, so we
    // only hit this catch for true failures. Max 3 auto-retries so a
    // genuinely broken config can still surface eventually.
    const _ESTIMATE_RESUME_KEY = (pid) => `pf.estimate.job.${pid}`;
    const runEstimate = async (payload) => {
        // When "Draw Takeoff" is ticked, run the takeoff FIRST so we can
        // feed the measured quantities (ground truth) into the estimate AI.
        // This closes the loop: shape on the drawing ↔ priced line in the
        // estimate both come from the same pixel-measured number.
        let takeoffItemsForEstimate = null;
        if (payload.draw_takeoff) {
            const items = await runTakeoff(payload);
            if (items && items.length > 0) {
                takeoffItemsForEstimate = items.map((it) => ({
                    id: it.id,
                    label: it.label,
                    type: it.type,
                    quantity_sf: it.quantity_sf,
                    quantity_lf: it.quantity_lf,
                    quantity_each: it.quantity_each,
                }));
            }
        }

        setEstimating(true);
        setJobStage("Queuing estimate…");
        setJobStagePct(0);
        setJobUsage(null);
        const stashJobId = (id) => {
            setActiveJobId(id);
            try {
                if (id) localStorage.setItem(_ESTIMATE_RESUME_KEY(projectId), id);
                else localStorage.removeItem(_ESTIMATE_RESUME_KEY(projectId));
            } catch { /* storage disabled, non-fatal */ }
        };
        const runOnce = async () => {
            return await createEstimate(
                {
                    ...payload,
                    project_id: projectId,
                    takeoff_items: takeoffItemsForEstimate,
                },
                handleJobStage,
                stashJobId,
            );
        };
        let record = null;
        // iter-206 — NO MORE FRONTEND RE-POSTING. The outer auto-retry
        // loop was causing a huge waste: every "retry" sent a fresh
        // POST which spun up a brand-new ai_jobs row and re-extracted
        // text from all 2000+ pages from scratch. The backend already
        // has FOUR layers of retry (shrink loop, batching fallback,
        // transient-error retries, server watchdog that resumes
        // interrupted jobs across restarts). Our job here is to fire
        // once and let the server work. Mobile users can close the
        // app, come back hours later, and the estimate keeps going.
        try {
            record = await runOnce();
            setEstimates((prev) => [record, ...prev]);
            setActiveEstimateId(record.id);
            if (takeoffItemsForEstimate) {
                toast.success(
                    `Estimate ready · ${takeoffItemsForEstimate.length} measured quantity${takeoffItemsForEstimate.length === 1 ? "" : "ies"} used as ground truth`,
                );
            } else {
                toast.success("Estimate ready");
            }
            if (payload.auto_pin && (record.auto_pinned_ids || []).length > 0) {
                toast.message(
                    `Pinned ${record.auto_pinned_ids.length} page${record.auto_pinned_ids.length === 1 ? "" : "s"} to the top`,
                );
                loadAll();
            }
        } catch (err) {
            if (err?.canceled) {
                toast.message("Estimate canceled");
            } else {
                const msg =
                    err?.response?.data?.detail || err?.message || "Estimate failed";
                toast.error(msg);
            }
        } finally {
            setEstimating(false);
            setJobStage("");
            setJobStagePct(null);
            stashJobId(null);
        }
    };

    // iter-202 — Resume a mid-flight estimate. If the user reloaded the
    // page or lost connection while an estimate was running, re-attach
    // to the SAME backend job and keep polling instead of forcing them
    // to start over. We check two sources, in order:
    //   1. localStorage pf.estimate.job.<projectId> (set when the UI
    //      launched the job in this browser)
    //   2. GET /ai-jobs/active?project_id=X (set by any browser/peer)
    const resumeActiveEstimate = useCallback(async () => {
        if (!projectId) return;
        try {
            let jobId = null;
            try { jobId = localStorage.getItem(_ESTIMATE_RESUME_KEY(projectId)) || null; } catch {}
            if (jobId) {
                // Verify it's still in-flight before attaching.
                try {
                    const j = await getAiJob(jobId);
                    if (!j || !["pending", "running", "interrupted"].includes(j.status)) {
                        jobId = null;
                        try { localStorage.removeItem(_ESTIMATE_RESUME_KEY(projectId)); } catch {}
                    }
                } catch {
                    jobId = null;
                }
            }
            if (!jobId) {
                const active = await getActiveAiJob(projectId, "estimate");
                if (active && ["pending", "running", "interrupted"].includes(active.status)) {
                    jobId = active.id;
                }
            }
            if (!jobId) return;

            toast.message("Reconnecting to running estimate…");
            setEstimating(true);
            setActiveJobId(jobId);
            setJobStage("Reconnecting to running estimate…");
            try { localStorage.setItem(_ESTIMATE_RESUME_KEY(projectId), jobId); } catch {}
            try {
                const result = await pollAiJob(jobId, { onStage: handleJobStage });
                // The /estimate route stores the final EstimateRecord
                // under `result` on the ai_jobs doc; refresh the list
                // so the new record appears in the estimate dropdown.
                setEstimates((prev) => {
                    if (!result || !result.id) return prev;
                    if (prev.some((r) => r.id === result.id)) return prev;
                    return [result, ...prev];
                });
                if (result && result.id) {
                    setActiveEstimateId(result.id);
                }
                toast.success("Estimate finished — reconnected successfully");
            } catch (err) {
                if (err?.canceled) {
                    toast.message("Estimate canceled");
                } else {
                    const msg = err?.response?.data?.detail || err?.message || "Estimate failed";
                    toast.error(msg);
                }
            } finally {
                setEstimating(false);
                setJobStage("");
                setJobStagePct(null);
                setActiveJobId(null);
                try { localStorage.removeItem(_ESTIMATE_RESUME_KEY(projectId)); } catch {}
            }
        } catch (e) {
            // non-fatal; silently skip resume
        }
    }, [projectId]);

    // File-management actions (toggle expand · split · pin · rename) —
    // factored into a hook (iter-104 refactor) so this shell stays
    // focused on layout + orchestration.
    const {
        togglePdfExpanded,
        handleSplitUpload,
        togglePin,
        handlePinSubpage,
        handleRenameUpload,
    } = useFileActions({
        setFiles,
        setExpandedPdfs,
        splittingIds,
        setSplittingIds,
        setPinningSubpage,
        setActiveFileId,
        setActivePage,
        loadAll: () => loadAll(),
    });

    // Flattened list of navigable pages in sidebar order. Each entry is
    // {fileId, page, label} so the Prev/Next buttons can step through every
    // pinned child, every split page, and every multi-page parent in a
    // single linear flow.
    const navPages = useMemo(() => {
        const out = [];
        const byParent = files.reduce((acc, f) => {
            if (f.parent_upload_id) {
                (acc[f.parent_upload_id] = acc[f.parent_upload_id] || []).push(f);
            }
            return acc;
        }, {});
        // Keep split-page children in page-number order under their parent.
        Object.values(byParent).forEach((arr) =>
            arr.sort((a, b) => (a.page_number || 0) - (b.page_number || 0)),
        );
        const seen = new Set();
        // Walk `files` which is already sorted pinned-first by rank.
        const pushFile = (f) => {
            if (seen.has(f.id)) return;
            seen.add(f.id);
            const displayLabel =
                f.display_name ||
                (f.parent_upload_id
                    ? `Page ${f.page_number || ""} · ${(
                          files.find((x) => x.id === f.parent_upload_id)
                              ?.original_filename || ""
                      ).replace(/\.pdf$/i, "")}`
                    : f.original_filename);
            out.push({
                fileId: f.id,
                page: 1,
                label: displayLabel,
            });
        };
        for (const f of files) {
            if (f.parent_upload_id) {
                // Pinned children land at the top (since `files` is pinned-first).
                if (f.pinned) pushFile(f);
                continue; // non-pinned children are added under their parent below
            }
            const kids = (byParent[f.id] || []).filter((c) => !c.pinned);
            // If this parent has been split into children (kids.length>0)
            // we SKIP the parent itself in Prev/Next navigation — walking
            // every split page directly is what the user actually wants.
            // Single-page files and un-split multi-page parents still
            // get included normally.
            const isSplitParent = kids.length > 0;
            if (!isSplitParent) pushFile(f);
            if (kids.length > 0) {
                for (const c of kids) pushFile(c);
            } else if (
                (f.content_type || "").toLowerCase() === "application/pdf" &&
                (f.page_count || 0) > 1
            ) {
                for (let p = 2; p <= f.page_count; p++) {
                    out.push({
                        fileId: f.id,
                        page: p,
                        label: `${f.original_filename} · Page ${p}`,
                    });
                }
            }
        }
        return out;
    }, [files]);

    const navIndex = navPages.findIndex(
        (n) => n.fileId === activeFileId && (n.page || 1) === (activePage || 1),
    );
    const goToNav = (delta) => {
        if (navPages.length === 0) return;
        let next = navIndex + delta;
        if (next < 0) next = 0;
        if (next > navPages.length - 1) next = navPages.length - 1;
        if (next === navIndex) return;
        const entry = navPages[next];
        setActiveFileId(entry.fileId);
        setActivePage(entry.page || 1);
    };

    // Drag-and-drop sensors: pointer for mouse, touch with long-press delay
    // (250ms) so swipe-scroll and tap still work on mobile.
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 250, tolerance: 8 },
        }),
    );

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = files.findIndex((f) => f.id === active.id);
        const newIndex = files.findIndex((f) => f.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        // Only allow reordering within the same pin group. If user drops across
        // groups, snap the dragged item to the nearest valid edge of its group.
        const activeFileObj = files[oldIndex];
        const targetFileObj = files[newIndex];
        if (activeFileObj.pinned !== targetFileObj.pinned) {
            toast.message("Pinned and unpinned items can't be mixed", {
                description: "Unpin first to move across groups.",
            });
            return;
        }
        const nextOrder = arrayMove(files, oldIndex, newIndex);
        setFiles(nextOrder);
        try {
            await reorderUploads(nextOrder.map((f) => f.id));
        } catch {
            toast.error("Couldn't save order");
            loadAll();
        }
    };

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const loadAll = async () => {
        try {
            const [p, f, a, e, t] = await Promise.all([
                getProject(projectId),
                listUploads(projectId),
                listAnalyses(projectId).catch(() => []),
                listEstimates(projectId).catch(() => []),
                listTakeoffItems(projectId).catch(() => []),
            ]);
            setProject(p);
            setFiles(f);
            setAnalyses(a || []);
            setEstimates(e || []);
            setTakeoffItems(t || []);
            if (f.length > 0 && !activeFileId) {
                // iter-168j/k — Prefer the *richest* as-built when
                // multiple exist for the same project (workgroup
                // absorb + older buggy inserts sometimes left dupes
                // hanging around). Score by number of regions with
                // uploaded storage_paths + filled elevation values;
                // ties broken by most-recent update. Falls back to
                // the previous first-file behaviour when no as-built
                // exists.
                const asbuilts = f.filter((x) => x.is_asbuilt);
                if (asbuilts.length > 0) {
                    const scored = asbuilts.map((a) => {
                        const flds = a.asbuilt_fields || {};
                        const regs = flds.regions || {};
                        const elev = flds.elevations || {};
                        const nRegs = Object.values(regs).filter(
                            (v) => v && v.storage_path,
                        ).length;
                        const nElev = Object.values(elev).filter(
                            (v) => v && String(v).trim(),
                        ).length;
                        return {
                            a,
                            score: nRegs * 10 + nElev,
                            ts: a.updated_at || a.created_at || "",
                        };
                    });
                    scored.sort((x, y) => {
                        if (y.score !== x.score) return y.score - x.score;
                        return String(y.ts).localeCompare(String(x.ts));
                    });
                    setActiveFileId(scored[0].a.id);
                } else {
                    // If the first file is a split parent, jump straight to its
                    // first child (split parents aren't viewable anymore).
                    const first = f[0];
                    const firstKids = f
                        .filter((x) => x.parent_upload_id === first.id)
                        .sort((a, b) => (a.page_number || 0) - (b.page_number || 0));
                    const target = firstKids.length > 0 ? firstKids[0] : first;
                    setActiveFileId(target.id);
                }
            }
        } catch (e) {
            toast.error("Couldn't load project workspace");
        } finally {
            setLoading(false);
        }
    };

    // Safety net: if the active file is a split parent (should be
    // unreachable via the UI now, but can happen via deep-links / state
    // restore), auto-jump to its first child page so the viewer doesn't
    // render a document that's been replaced by its per-page children.
    useEffect(() => {
        if (!activeFileId || files.length === 0) return;
        const active = files.find((f) => f.id === activeFileId);
        if (!active) return;
        const kids = files
            .filter((f) => f.parent_upload_id === active.id)
            .sort((a, b) => (a.page_number || 0) - (b.page_number || 0));
        if (kids.length > 0) {
            setActiveFileId(kids[0].id);
            setActivePage(1);
        }
    }, [activeFileId, files, setActiveFileId, setActivePage]);

    useEffect(() => {
        if (projectId) loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // iter-202 — After mount, try to resume an in-flight estimate for
    // this project (e.g. after a page reload or a network drop mid-run).
    useEffect(() => {
        if (!projectId) return;
        resumeActiveEstimate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // Deep-link: when the user clicks a "measured" pill on the Reports page,
    // we arrive here at /workspace/:id?takeoff=<takeoff_id>. After the project
    // finishes loading, jump to the shape's page and select it on the canvas.
    useEffect(() => {
        const tid = searchParams.get("takeoff");
        if (!tid || takeoffItems.length === 0) return;
        const item = takeoffItems.find((x) => x.id === tid);
        if (!item) return;
        setActiveFileId(item.upload_id);
        setActivePage(1);
        setSelectedTakeoffId(item.id);
        // Consume the param so a refresh doesn't re-trigger this effect and
        // the user can freely navigate away without a sticky query.
        const next = new URLSearchParams(searchParams);
        next.delete("takeoff");
        setSearchParams(next, { replace: true });
        toast.success(`Jumped to measured shape · ${item.label}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [takeoffItems, searchParams]);

    // Auto-expand the active PDF's page list so the user can see their context.
    useEffect(() => {
        if (!activeFile || !isPdf(activeFile) || (activeFile.page_count || 0) <= 1) return;
        setExpandedPdfs((prev) => {
            if (prev.has(activeFile.id)) return prev;
            const next = new Set(prev);
            next.add(activeFile.id);
            return next;
        });
    }, [activeFile]);

    // Whenever the mobile sidebar opens OR the active selection changes,
    // scroll the active document (and its active page) into view inside both
    // the desktop aside AND the mobile Sheet copies of the list.
    useEffect(() => {
        if (!activeFile) return;
        const doScroll = () => {
            const subSel = `[data-testid="workspace-subpage-${activeFile.id}-${activePage}"]`;
            const fileSel = `[data-testid="workspace-page-${activeFile.id}"]`;
            let targets = document.querySelectorAll(subSel);
            if (targets.length === 0) targets = document.querySelectorAll(fileSel);
            targets.forEach((el) =>
                el.scrollIntoView({ block: "center", behavior: "smooth" }),
            );
        };
        // Delay covers Sheet entry animation + subpage list render after expand.
        const t = setTimeout(doScroll, 260);
        return () => clearTimeout(t);
    }, [sidebarOpen, activeFile, activePage]);

    // Arrow-key shortcuts for prev/next page. Only when not typing in an
    // input/textarea so it doesn't interfere with forms.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            const tag = (e.target?.tagName || "").toLowerCase();
            if (["input", "textarea", "select"].includes(tag)) return;
            if (e.target?.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (navPages.length < 2) return;
            e.preventDefault();
            goToNav(e.key === "ArrowRight" ? 1 : -1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navPages, navIndex]);

    const token = (() => {
        try {
            return localStorage.getItem("planflow.auth_token") || "";
        } catch {
            return "";
        }
    })();
    const fileUrl = activeFile
        ? (() => {
              // iter-167 — HEIC/HEIF from iPhones can't render natively;
              // ask the backend to transcode to JPEG via ?render=web.
              const name = (activeFile.original_filename || "").toLowerCase();
              const needsTranscode = name.endsWith(".heic") || name.endsWith(".heif");
              const params = new URLSearchParams();
              if (token) params.set("token", token);
              if (needsTranscode) params.set("render", "web");
              // iter-169e — bust the browser/pdf.js cache when a
              // schedule PDF was edited so re-opening picks up the
              // latest field values baked into /V.
              if (activeFile.schedule_no && activeFile.updated_at) {
                  params.set("v", activeFile.updated_at);
              }
              const qs = params.toString();
              return `${backendUrl}/api/files/${activeFile.storage_path}${qs ? `?${qs}` : ""}`;
          })()
        : null;

    const handleConfirmDelete = async () => {
        if (!pendingDeleteFile) return;
        setDeletingFile(true);
        try {
            await deleteUpload(pendingDeleteFile.id);
            const remaining = files.filter((f) => f.id !== pendingDeleteFile.id);
            setFiles(remaining);
            if (activeFileId === pendingDeleteFile.id) {
                setActiveFileId(remaining[0]?.id || null);
            }
            toast.success(`Deleted ${pendingDeleteFile.original_filename}`);
            setPendingDeleteFile(null);
        } catch (e) {
            toast.error("Couldn't delete document");
        } finally {
            setDeletingFile(false);
        }
    };

    const SidebarContent = ({ onNavigate }) => {
        // iter-45: Pinned split-page children stay UNDER their parent now;
        // no more promotion to top level (users found the duplicate rows
        // confusing). Top level = parents only.
        const parents = files.filter((f) => !f.parent_upload_id);
        const topLevel = [...parents].sort((a, b) => {
            if ((b.pinned ? 1 : 0) !== (a.pinned ? 1 : 0))
                return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
            if ((b.rank || 0) !== (a.rank || 0))
                return (b.rank || 0) - (a.rank || 0);
            return (b.created_at || "").localeCompare(a.created_at || "");
        });
        // Children shown under their parent when expanded (pinned or not).
        const childrenByParent = files.reduce((acc, f) => {
            if (f.parent_upload_id) {
                (acc[f.parent_upload_id] = acc[f.parent_upload_id] || []).push(f);
            }
            return acc;
        }, {});
        Object.keys(childrenByParent).forEach((pid) => {
            childrenByParent[pid].sort(
                (a, b) => (a.page_number || 0) - (b.page_number || 0),
            );
        });
        const expandableIds = parents
            .filter(
                (f) =>
                    (childrenByParent[f.id]?.length || 0) > 0 ||
                    (isPdf(f) && (f.page_count || 0) > 1),
            )
            .map((f) => f.id);
        const allExpanded =
            expandableIds.length > 0 &&
            expandableIds.every((id) => expandedPdfs.has(id));
        const anyExpanded = expandableIds.some((id) => expandedPdfs.has(id));

        return (
        <>
            <div className="px-3 py-2 border-b border-[#2A3655] bg-gradient-to-b from-[#0E1A3A] to-[#0A1128]">
                <div className="flex items-center gap-2 pr-10">
                    <BookMarked className="w-4 h-4 text-[#3395FF]" />
                    <div className="text-[#3395FF] font-bold text-[13px] tracking-tight">
                        Pages, Bookmarks
                    </div>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500 tabular-nums">
                        {files.length}
                    </span>
                </div>
                {/* Iter-31: per-row action icons moved here. Tools apply to
                    the currently-selected file (active file id). Keeping
                    the rows themselves to just "name + badges" maximizes
                    filename width on the narrow sidebar. */}
                <div className="mt-2">
                    <DocsToolbar
                        activeFile={files.find((f) => f.id === activeFileId) || null}
                        activePage={activePage}
                        onRename={(f) => setRenamingFileId(f.id)}
                        onTogglePin={(f) => togglePin(f)}
                        onPinPage={(f, p) => handlePinSubpage(f, p)}
                        pinningSubpage={pinningSubpage}
                        onSplit={(f) => handleSplitUpload(f)}
                        onDelete={(f) => setPendingDeleteFile(f)}
                        splittingIds={splittingIds}
                    />
                </div>
                {/* Planswift "Search:" field — filters both file rows and
                    their inline takeoff items by name match. */}
                <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                        Search:
                    </span>
                    <div className="flex-1 relative">
                        <Search className="w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                        <input
                            type="text"
                            value={sidebarSearch}
                            onChange={(e) => setSidebarSearch(e.target.value)}
                            placeholder=""
                            data-testid="workspace-sidebar-search"
                            className="w-full pl-6 pr-6 py-1 bg-[#05091A] border border-[#2A3655] focus:border-[#3395FF] rounded-sm text-[11px] text-white outline-none"
                        />
                        {sidebarSearch && (
                            <button
                                type="button"
                                onClick={() => setSidebarSearch("")}
                                data-testid="workspace-sidebar-search-clear"
                                aria-label="Clear search"
                                className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded-sm hover:bg-rose-500/20 text-rose-400"
                            >
                                <XIcon className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>
                {expandableIds.length > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold">
                        <button
                            type="button"
                            onClick={() => setExpandedPdfs(new Set(expandableIds))}
                            disabled={allExpanded}
                            data-testid="btn-expand-all"
                            className="flex items-center gap-1 px-2 py-1 rounded-sm text-slate-300 hover:text-white hover:bg-[#1C253C] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-300 transition-colors"
                        >
                            <ChevronsUpDown className="w-3 h-3 text-[#00A3FF]" />
                            Expand all
                        </button>
                        <button
                            type="button"
                            onClick={() => setExpandedPdfs(new Set())}
                            disabled={!anyExpanded}
                            data-testid="btn-collapse-all"
                            className="flex items-center gap-1 px-2 py-1 rounded-sm text-slate-300 hover:text-white hover:bg-[#1C253C] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-300 transition-colors"
                        >
                            <ChevronsDownUp className="w-3 h-3 text-[#00A3FF]" />
                            Collapse all
                        </button>
                    </div>
                )}
            </div>
            <ul className="flex-1 min-h-0 overflow-y-auto py-2 space-y-1" data-testid="workspace-pages-list">
                {loading && <li className="px-4 py-2 text-sm text-slate-400">Loading…</li>}
                {!loading && files.length === 0 && (
                    <li className="px-4 py-3 text-xs text-slate-400">
                        No documents yet. Use "Add Files" below.
                    </li>
                )}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={topLevel.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {topLevel.map((f) => {
                            // Sidebar search: keep rows whose display name,
                            // original filename, or any nested takeoff
                            // label matches the query. Empty query = keep
                            // everything.
                            const q = sidebarSearch.trim().toLowerCase();
                            if (q) {
                                const hit = (s) =>
                                    (s || "").toLowerCase().includes(q);
                                const nameHit =
                                    hit(f.display_name) ||
                                    hit(f.original_filename);
                                const ownT = takeoffItemsByUploadId[f.id] || [];
                                const childIds = (
                                    childrenByParent[f.id] || []
                                ).map((c) => c.id);
                                const childT = childIds.flatMap(
                                    (id) => takeoffItemsByUploadId[id] || [],
                                );
                                const takeoffHit = [...ownT, ...childT].some(
                                    (t) => hit(t.name) || hit(t.label),
                                );
                                const childNameHit = (
                                    childrenByParent[f.id] || []
                                ).some(
                                    (c) =>
                                        hit(c.display_name) ||
                                        hit(`page ${c.page_number}`),
                                );
                                if (!nameHit && !takeoffHit && !childNameHit)
                                    return null;
                            }
                            // Promoted pinned child → render a slimmer row
                            // with the parent filename + page number.
                            if (f.parent_upload_id) {
                                const parent = files.find(
                                    (x) => x.id === f.parent_upload_id,
                                );
                                return (
                                    <SortablePinnedChildRow
                                        key={f.id}
                                        child={f}
                                        parent={parent}
                                        active={activeFileId === f.id}
                                        onPickFile={() => {
                                            setActiveFileId(f.id);
                                            setActivePage(1);
                                            onNavigate && onNavigate();
                                        }}
                                        onRename={handleRenameUpload}
                                        renamingFileId={renamingFileId}
                                        onRenameRequest={setRenamingFileId}
                                    />
                                );
                            }
                            const active = activeFile?.id === f.id;
                            const isPdfFile = isPdf(f);
                            const pageCount = f.page_count || 0;
                            const childRows = childrenByParent[f.id] || [];
                            const canExpand =
                                childRows.length > 0 || (isPdfFile && pageCount > 1);
                            const expanded = canExpand && expandedPdfs.has(f.id);
                            const needsSplit =
                                isPdfFile && pageCount > 1 && childRows.length === 0;
                            // When a PDF has been split, its parent row is
                            // no longer a valid viewing target — clicking
                            // the filename expands/collapses instead of
                            // navigating. Single-page files and un-split
                            // PDFs still navigate on click as normal.
                            const isSplitParent = childRows.length > 0;
                            // Takeoffs that belong to this document OR any
                            // of its children (pages). These render as
                            // indented sub-rows directly beneath the parent
                            // — Planswift-style — so the sidebar doubles
                            // as both the document tree AND the takeoff
                            // list, no separate section.
                            const ownTakeoffs = takeoffItemsByUploadId[f.id] || [];
                            const childUploadIds = childRows.map((c) => c.id);
                            const childTakeoffs = childUploadIds.flatMap(
                                (id) => takeoffItemsByUploadId[id] || [],
                            );
                            const docTakeoffs = ownTakeoffs.concat(childTakeoffs);
                            return (
                                <SortableDocRow
                                    key={f.id}
                                    file={f}
                                    active={active}
                                    canExpand={canExpand}
                                    expanded={expanded}
                                    pageCount={pageCount}
                                    isPdfFile={isPdfFile}
                                    isSplitParent={isSplitParent}
                                    activePage={activePage}
                                    activeFileId={activeFileId}
                                    childRows={childRows}
                                    needsSplit={needsSplit}
                                    splitting={splittingIds.has(f.id)}
                                    takeoffItems={docTakeoffs}
                                    selectedTakeoffId={selectedTakeoffId}
                                    onSelectTakeoff={setSelectedTakeoffId}
                                    onDeleteTakeoff={handleDeleteTakeoffItem}
                                    onToggleTakeoffVisibility={handleToggleTakeoffVisibility}
                                    onJumpToTakeoff={(t) => {
                                        setActiveFileId(t.upload_id);
                                        setActivePage(1);
                                        setSelectedTakeoffId(t.id);
                                        onNavigate && onNavigate();
                                    }}
                                    onSplit={() => handleSplitUpload(f)}
                                    onPickFile={(targetId) => {
                                        setActiveFileId(targetId || f.id);
                                        setActivePage(1);
                                        onNavigate && onNavigate();
                                    }}
                                    onPickPage={(p) => {
                                        setActiveFileId(f.id);
                                        setActivePage(p);
                                        onNavigate && onNavigate();
                                    }}
                                    onToggleExpand={() => togglePdfExpanded(f.id)}
                                    onTogglePin={(target) => togglePin(target || f)}
                                    onDelete={(target) => setPendingDeleteFile(target || f)}
                                    onRename={handleRenameUpload}
                                    renamingFileId={renamingFileId}
                                    onRenameRequest={setRenamingFileId}
                                />
                            );
                        })}
                    </SortableContext>
                </DndContext>
            </ul>

            {/* Takeoff shapes are now rendered inline under each document
                row via SortableDocRow — Planswift-style. The separate
                TakeoffItemsPanel was hiding the document list and has been
                retired. */}

            <div className="border-t border-[#2A3655] p-2 space-y-1.5">
                <button
                    type="button"
                    onClick={() => { setUploadOpen(true); onNavigate && onNavigate(); }}
                    data-testid="workspace-btn-upload"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-semibold transition-colors active:scale-[0.98]"
                >
                    <CloudUpload className="w-4 h-4" />
                    Add Files
                </button>
            </div>
        </>
        );
    };

    return (
        <div className="h-[calc(100dvh-var(--pf-imp-banner-h,0px))] overflow-hidden blueprint-bg text-slate-100 flex flex-col">
            <TopNav active="projects" onTabChange={(k) => { if (k === "projects") navigate("/"); }} />

            {/* Breadcrumb / project header */}
            <div className="border-b border-[#2A3655] bg-[#0A1128]/80 backdrop-blur-xl">
                <div className="max-w-[1600px] mx-auto flex items-center justify-between px-3 md:px-6 py-2 gap-2 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            onClick={() => navigate("/")}
                            data-testid="btn-back-home"
                            className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Home
                        </button>
                        {/* iter-201 — Mobile/tablet-portrait trigger to open
                            the file list. The desktop sidebar uses
                            `hidden md:flex`, so below 768px CSS width the
                            whole file list was invisible with no way to
                            reach it. This Sheet re-exposes it. */}
                        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                            <SheetTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="btn-open-sidebar-mobile"
                                    aria-label="Open file list"
                                    className="md:hidden inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors px-2 py-1 rounded-sm border border-[#2A3655] bg-[#1C253C]"
                                >
                                    <Menu className="w-4 h-4" />
                                    <span className="font-bold uppercase tracking-wider text-[10px]">Pages</span>
                                </button>
                            </SheetTrigger>
                            <SheetContent
                                side="left"
                                className="bg-[#131A2F] border-r border-[#2A3655] text-slate-100 p-0 w-[85vw] max-w-sm flex flex-col"
                                data-testid="mobile-sidebar-sheet"
                            >
                                <VisuallyHidden.Root>
                                    <SheetTitle>Project files</SheetTitle>
                                    <SheetDescription>Pages, bookmarks and uploaded files for this project</SheetDescription>
                                </VisuallyHidden.Root>
                                <SidebarContent onNavigate={() => setSidebarOpen(false)} />
                            </SheetContent>
                        </Sheet>
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
                        <span
                            className="text-sm font-semibold text-white truncate"
                            data-testid="workspace-project-name"
                            title={project?.name}
                        >
                            {project?.name || "Loading…"}
                        </span>
                        {project?.job_id && (
                            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#007AFF]/15 border border-[#007AFF]/40 text-[#00A3FF]">
                                {project.job_id}
                            </span>
                        )}
                        {/* iter-168d — Contact link chip. Opens a small
                            inline picker. When set, the As-Built Email
                            button auto-fills the recipient with the
                            contact's email address. */}
                        {project && (
                            <ProjectContactChip
                                project={project}
                                onChange={(updated) => setProject(updated)}
                            />
                        )}
                    </div>
                    <div
                        className="hidden md:flex items-center gap-3 text-xs text-slate-400"
                        data-testid="workspace-header-right"
                    >
                        <button
                            type="button"
                            onClick={() => setFilesOpen(true)}
                            data-testid="btn-open-project-files"
                            title="Project files — every upload, in one place"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] hover:text-[#00A3FF] text-slate-300 transition-colors"
                        >
                            <FolderOpen className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline font-bold uppercase tracking-wider text-[10px]">
                                Files
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setPhotosOpen(true)}
                            data-testid="btn-open-project-photos"
                            title="Job-site photos"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] hover:text-[#00A3FF] text-slate-300 transition-colors"
                        >
                            <Camera className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline font-bold uppercase tracking-wider text-[10px]">
                                Photos
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setReceiptsOpen(true)}
                            data-testid="btn-open-project-receipts"
                            title="Receipts for this job"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] hover:text-[#00A3FF] text-slate-300 transition-colors"
                        >
                            <Receipt className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline font-bold uppercase tracking-wider text-[10px]">
                                Receipts
                            </span>
                        </button>
                        <div
                            className="flex items-center gap-1"
                            data-testid="workspace-unit-system"
                        >
                            <Ruler className="w-3.5 h-3.5" />
                            <span className="font-medium uppercase tracking-wider">
                                {project?.unit_system || "imperial"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <AIToolbar
                files={files}
                analyses={analyses}
                estimates={estimates}
                onOpenAnalyze={() => setAnalyzeOpen(true)}
                onOpenEstimate={() => setEstimateOpen(true)}
                onOpenAsBuilt={handleOpenAsBuilt}
                hasAsBuilt={files.some((f) => f.is_asbuilt)}
                asBuiltLoading={asBuiltLoading}
                onOpenSchedule={handleOpenSchedule}
                scheduleLoading={scheduleLoading}
                onOpenSeptic={() => setSepticOpen(true)}
                onPrintPack={handlePrintPack}
                hasSchedules={files.some((f) => f.schedule_no)}
                onPickAnalysis={setActiveAnalysisId}
                onPickEstimate={setActiveEstimateId}
            />

            <div className="flex-1 min-h-0 flex">
                {/* Left sidebar (md+) */}
                <aside className="hidden md:flex w-64 flex-col bg-[#131A2F] border-r border-[#2A3655]">
                    <SidebarContent />
                </aside>

                <WorkspaceCanvas
                    loading={loading}
                    activeFile={activeFile}
                    fileUrl={fileUrl}
                    backendUrl={backendUrl}
                    token={token}
                    onAsBuiltUpdated={(updated) =>
                        setFiles((prev) => prev.map((f) => (f.id === updated.id ? updated : f)))
                    }
                    activePage={activePage}
                    project={project}
                    navPages={navPages}
                    navIndex={navIndex}
                    goToNav={goToNav}
                    onUploadClick={() => setUploadOpen(true)}
                    onScaleChange={(updated) =>
                        setFiles((prev) =>
                            prev.map((f) => (f.id === updated.id ? updated : f)),
                        )
                    }
                    takeoffItems={
                        activeFile
                            ? (takeoffItemsByUploadId[activeFile.id] || []).filter(
                                  (t) => !t.hidden,
                              )
                            : []
                    }
                    selectedTakeoffId={selectedTakeoffId}
                    onSelectTakeoff={setSelectedTakeoffId}
                    onCommitTakeoffPoints={handleCommitTakeoffPoints}
                    canUndoTakeoff={takeoffHistory.length > 0}
                    onUndoTakeoff={handleUndoTakeoff}
                    undoingTakeoff={undoing}
                    drawMode={drawMode}
                    drawTrade={drawTrade}
                    drawPoints={drawPoints}
                    drawSaving={drawSaving}
                    onDrawModeChange={setDrawMode}
                    onDrawTradeChange={setDrawTrade}
                    onDrawPoint={handleDrawPoint}
                    onDrawFinish={handleDrawFinish}
                    onDrawCancel={handleDrawCancel}
                    onPageSize={setPdfPageSize}
                    onScheduleFieldChange={handleScheduleFieldChange}
                    onPrintSchedule={handlePrintSchedule}
                    onSaveSignature={handleSaveSignature}
                />
            </div>

            <UploadPlansDialog
                open={uploadOpen}
                onOpenChange={setUploadOpen}
                projectId={projectId}
                onUploaded={() => loadAll()}
                onDeleted={() => loadAll()}
                onCreated={() => loadAll()}
            />

            <PrintPreviewDialog
                open={printPreview.open}
                blobUrl={printPreview.url}
                filename={printPreview.filename}
                onDownload={handleDownloadFlat}
                onOpenChange={(v) => { if (!v) handleClosePrintPreview(); }}
            />

            <SepticDesignSheet
                open={septicOpen}
                onOpenChange={setSepticOpen}
                project={project}
                onSaved={handleSaveSepticDesign}
            />

            <AnalyzeDialog
                open={analyzeOpen}
                onOpenChange={setAnalyzeOpen}
                files={files}
                onSubmit={runAnalyze}
                projectId={projectId}
            />

            <AnalyzingOverlay
                open={analyzing || estimating || takingOff}
                kind={estimating ? "estimate" : takingOff ? "takeoff" : "analyze"}
                stage={jobStage}
                stagePct={jobStagePct}
                usage={jobUsage}
                pagesScanned={jobPages.scanned}
                pagesTotal={jobPages.total}
                canCancel={!!activeJobId}
                onCancel={async () => {
                    if (!activeJobId) return;
                    try {
                        await cancelAiJob(activeJobId);
                        toast.message("Cancel requested — wrapping up…");
                    } catch (err) {
                        toast.error(
                            err?.response?.data?.detail ||
                                err?.message ||
                                "Couldn't cancel the job",
                        );
                    }
                }}
            />

            <AnalysisResultDialog
                open={!!activeAnalysisId}
                onOpenChange={(v) => !v && setActiveAnalysisId(null)}
                analysis={activeAnalysis}
                onDeleted={(deletedId) => {
                    setAnalyses((prev) => prev.filter((a) => a.id !== deletedId));
                    setActiveAnalysisId(null);
                }}
            />

            <EstimateDialog
                open={estimateOpen}
                onOpenChange={setEstimateOpen}
                files={files}
                onSubmit={runEstimate}
                projectId={projectId}
            />

            <EstimateResultDialog
                open={!!activeEstimateId}
                onOpenChange={(v) => !v && setActiveEstimateId(null)}
                estimate={activeEstimate}
                project={project}
                onDeleted={() => {
                    setEstimates((prev) => prev.filter((x) => x.id !== activeEstimateId));
                    setActiveEstimateId(null);
                }}
                onUpdated={(updated) => {
                    // Keep the parent estimates array in sync so re-opening
                    // the dialog shows the freshest persisted values
                    // (line_total edits, notes, cleaned markdown).
                    setEstimates((prev) =>
                        prev.map((e) => (e.id === updated.id ? updated : e)),
                    );
                }}
                onCreated={(created) => {
                    // iter-110: Retry-with-different-strategy created a
                    // sibling estimate. Prepend to the list and switch
                    // focus so the user sees the new numbers immediately.
                    setEstimates((prev) => [created, ...prev]);
                    setActiveEstimateId(created.id);
                }}
            />

            {/* Per-project file folder — any file type, shared with the
                project's workgroup automatically. */}
            <ProjectFilesPanel
                projectId={projectId}
                open={filesOpen}
                onOpenChange={setFilesOpen}
                kind="file"
            />
            {/* iter-98: dedicated Photos drawer (job-site images). */}
            <ProjectFilesPanel
                projectId={projectId}
                open={photosOpen}
                onOpenChange={setPhotosOpen}
                kind="photo"
            />
            {/* iter-98: dedicated Receipts drawer. */}
            <ProjectFilesPanel
                projectId={projectId}
                open={receiptsOpen}
                onOpenChange={setReceiptsOpen}
                kind="receipt"
            />

            {/* iter-47: plugin bar is now docked above the main workspace
                area (see mount point near the AIToolbar). */}

            <AlertDialog
                open={!!pendingDeleteFile}
                onOpenChange={(o) => !o && !deletingFile && setPendingDeleteFile(null)}
            >
                <AlertDialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[460px]"
                    data-testid="workspace-delete-doc-confirm"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this document?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            <span className="text-white font-semibold break-all">
                                {pendingDeleteFile?.original_filename}
                            </span>
                            {" "}will be permanently removed from this project. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="workspace-cancel-delete-doc"
                            className="bg-[#1C253C] border-[#2A3655] text-slate-300 hover:bg-[#2A3655] hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            disabled={deletingFile}
                            data-testid="workspace-confirm-delete-doc"
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            {deletingFile ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// iter-168d — Contact link chip. Tiny inline picker that lives beside
// the project name. Choosing a contact PATCHes the project and pushes
// the result back up. The As-Built Email dialog reads the linked
// contact's email address to auto-fill the recipient field.
function ProjectContactChip({ project, onChange }) {
    const [open, setOpen] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    // iter-168u — Anchor the popover to the button's screen rect via
    // getBoundingClientRect so we can render it in a portal at
    // position: fixed. Without this the dropdown got clipped behind
    // the workspace canvas (parent chain has overflow: hidden and its
    // own stacking context, so z-50 alone doesn't escape).
    const btnRef = useRef(null);
    const [anchor, setAnchor] = useState(null);
    useEffect(() => {
        if (!open || !btnRef.current) return;
        const update = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            setAnchor({ left: r.left + r.width / 2, top: r.bottom + 4 });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const rows = await listContacts();
                setContacts(rows || []);
            } catch { /* silent */ }
        })();
    }, [open]);

    const linked = useMemo(
        () => contacts.find((c) => c.id === project?.contact_id) || null,
        [contacts, project?.contact_id],
    );
    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return contacts.slice(0, 20);
        return contacts.filter((c) => (
            (c.name || "").toLowerCase().includes(term)
            || (c.email || "").toLowerCase().includes(term)
        )).slice(0, 20);
    }, [contacts, q]);

    const pick = async (contactId) => {
        if (!project?.id || saving) return;
        setSaving(true);
        try {
            const updated = await updateProject(project.id, { contact_id: contactId });
            onChange && onChange(updated);
            setOpen(false);
        } catch {
            // eslint-disable-next-line no-alert
            alert("Couldn't update project contact.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="relative">
            <button
                ref={btnRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="workspace-contact-link-chip"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold transition-colors ${
                    linked
                        ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-300 hover:border-emerald-400"
                        : "border-[#2A3655] text-slate-400 hover:text-[#00A3FF] hover:border-[#00A3FF]"
                }`}
                title={linked ? `Linked to ${linked.name || linked.email}` : "Link a contact"}
            >
                {linked
                    ? (<><Users className="w-3 h-3" /><span className="max-w-[140px] truncate normal-case tracking-normal font-semibold">{linked.name || linked.email}</span></>)
                    : (<><UserPlus className="w-3 h-3" /><span>Link contact</span></>)
                }
            </button>
            {open && anchor && createPortal(
                <>
                    <div
                        className="fixed inset-0 z-[9998]"
                        onClick={() => setOpen(false)}
                        data-testid="workspace-contact-link-backdrop"
                    />
                    <div
                        className="fixed z-[9999] w-72 bg-white rounded shadow-xl border border-slate-200 p-2"
                        style={{
                            left: `${anchor.left}px`,
                            top: `${anchor.top}px`,
                            transform: "translateX(-50%)",
                        }}
                        data-testid="workspace-contact-link-menu"
                    >
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search contacts…"
                        className="w-full text-xs border border-slate-300 rounded px-2 py-1 text-slate-900 mb-1"
                    />
                    <div className="max-h-56 overflow-y-auto">
                        {filtered.length === 0 && (
                            <div className="text-[11px] text-slate-500 px-2 py-3 text-center">No contacts found.</div>
                        )}
                        {filtered.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                disabled={saving}
                                onClick={() => pick(c.id)}
                                className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-slate-100 ${
                                    c.id === project?.contact_id ? "bg-emerald-50 text-emerald-900" : "text-slate-800"
                                }`}
                                data-testid={`workspace-contact-option-${c.id}`}
                            >
                                <div className="font-semibold truncate">{c.name || c.email || "Untitled"}</div>
                                {c.email && <div className="text-[10px] text-slate-500 truncate">{c.email}</div>}
                            </button>
                        ))}
                    </div>
                    {linked && (
                        <button
                            type="button"
                            onClick={() => pick(null)}
                            disabled={saving}
                            className="mt-1 w-full text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                            data-testid="workspace-contact-unlink"
                        >
                            Unlink contact
                        </button>
                    )}
                    </div>
                </>,
                document.body,
            )}
        </div>
    );
}

